import { useEffect, useState } from "react";
import { Zap, Gift, TrendingUp, Droplets, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { TokenomicsChart } from "./TokenomicsChart";
import { AIRDROP_TIERS, PRESALE_CONFIG } from "@/lib/presale";
import { getCachedTotalLp } from "@/lib/lpTracker";

export function Tokenomics() {
  const [totalLp, setTotalLp] = useState<number | null>(null);

  useEffect(() => {
    // Fetch total LP
    async function fetchLp() {
      const lp = await getCachedTotalLp();
      setTotalLp(lp);
    }
    fetchLp();

    // Refresh LP every 30 seconds
    const interval = setInterval(fetchLp, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full py-16 px-4 bg-gradient-to-b from-transparent to-purple-900/10">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Zap className="w-8 h-8 text-purple-400" />
            <h2 className="text-4xl font-bold text-white">Tokenomics</h2>
          </div>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Transparent and fair token distribution designed for long-term growth and sustainability.
            50% goes to the community through airdrops, ensuring widespread ownership from day one.
          </p>
        </div>

        {/* Pie Chart */}
        <div className="mb-12">
          <TokenomicsChart />
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="bg-card/50 backdrop-blur-sm border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 hover:scale-105">
            <CardContent className="pt-6 text-center">
              <div className="flex items-center justify-center mb-4">
                <Zap className="w-8 h-8 text-purple-400" />
              </div>
              <div className="text-4xl font-bold text-purple-400 mb-2">1B</div>
              <div className="text-gray-400 font-medium">Total Supply</div>
              <p className="text-sm text-gray-500 mt-2">Fixed supply for predictable economics</p>
            </CardContent>
          </Card>

          <Card className="bg-card/50 backdrop-blur-sm border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 hover:scale-105">
            <CardContent className="pt-6 text-center">
              <div className="flex items-center justify-center mb-4">
                <TrendingUp className="w-8 h-8 text-violet-400" />
              </div>
              <div className="text-4xl font-bold text-violet-400 mb-2">200M</div>
              <div className="text-gray-400 font-medium">Presale Allocation</div>
              <p className="text-sm text-gray-500 mt-2">20% available during 48-hour presale</p>
            </CardContent>
          </Card>

          <Card className="bg-card/50 backdrop-blur-sm border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 hover:scale-105">
            <CardContent className="pt-6 text-center">
              <div className="flex items-center justify-center mb-4">
                <Droplets className="w-8 h-8 text-indigo-400" />
              </div>
              <div className="text-4xl font-bold text-indigo-400 mb-2">
                {totalLp !== null ? `${totalLp.toFixed(1)} SOL` : "..."}
              </div>
              <div className="text-gray-400 font-medium">Total LP</div>
              <p className="text-sm text-gray-500 mt-2">
                {PRESALE_CONFIG.teamInitialLp} SOL Team Initial LP
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Airdrop Tiers */}
        <Card className="bg-card/50 backdrop-blur-sm border-purple-500/20 mb-12">
          <CardContent className="pt-6">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Gift className="w-6 h-6 text-purple-400" />
              Airdrop Tiers (500M Total)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(AIRDROP_TIERS).map(([key, tier]) => (
                <div
                  key={key}
                  className="p-4 rounded-lg border-2 border-purple-500/20 bg-gradient-to-br from-gray-900/50 to-purple-900/10 hover:border-purple-500/40 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-lg font-bold text-white">{tier.name}</span>
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: tier.color }}
                    />
                  </div>
                  <div className="text-3xl font-bold text-purple-400 mb-1">
                    {tier.amount.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-400">$PULSE</div>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-400 mt-4 text-center">
              Total airdrop allocation: 500M $PULSE (50% of total supply)
            </p>
          </CardContent>
        </Card>

        {/* Distribution Details */}
        <Card className="bg-card/50 backdrop-blur-sm border-purple-500/20">
          <CardContent className="pt-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-400" />
              Distribution Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div>
                <h4 className="font-semibold text-purple-400 mb-2 flex items-center gap-2">
                  <Gift className="w-4 h-4" />
                  Airdrop (50% - 500M)
                </h4>
                <p className="text-gray-400">
                  Distributed to community members across three tiers: Platinum (1.64M),
                  Purple (420k), and Black (90k). This ensures widespread ownership and
                  community engagement from launch.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-violet-400 mb-2 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Presale (20% - 200M)
                </h4>
                <p className="text-gray-400">
                  Available during a 48-hour presale window. Presale buyers receive a 40%
                  discount compared to initial listing price. 100% of presale funds go
                  directly into liquidity.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-indigo-400 mb-2 flex items-center gap-2">
                  <Droplets className="w-4 h-4" />
                  Liquidity Pool (20% - 200M)
                </h4>
                <p className="text-gray-400">
                  Initial LP of $50,000 paired with 200M tokens. LP deployed on Meteora
                  with phased rollout (20% per day for 5 days) to ensure price stability.
                  All LP fees during phased deployment used for buybacks & burns.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-purple-300 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Team & Marketing (10% - 100M)
                </h4>
                <p className="text-gray-400">
                  Vested for 1 year to ensure long-term commitment. First 3 months: 90%
                  of PulseAPP revenue + 90% of Pulse Cards royalties used for buybacks & burns.
                  After that: 50% ongoing.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
